import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCreateProject } from "@/hooks/useProjects";
import { WIZARD_QUESTIONS } from "@shared/projectConstants";
import { toast } from "sonner";

const DEAL_TYPES = [
  { value: "M&A", label: "M&A (Sell-Side)", description: "Full sell-side process with QoE, CIM, go-to-market" },
  { value: "Capital Raise", label: "Capital Raise", description: "Equity raise, growth capital, or recapitalization" },
  { value: "Debt Placement", label: "Debt Placement", description: "Senior debt, mezzanine, or refinancing" },
  { value: "Restructuring", label: "Restructuring", description: "Financial or operational restructuring" },
  { value: "ESOP", label: "ESOP", description: "Employee stock ownership plan transaction" },
  { value: "Buy-Side Advisory", label: "Buy-Side Advisory", description: "Acquisition search and advisory" },
];

const DEAL_SIZES = ["Under $10M", "$10M – $25M", "$25M – $50M", "$50M – $100M", "$100M – $250M", "$250M+"];

interface ProjectWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedCompanyId?: string;
  preselectedCompanyName?: string;
  preselectedDealType?: string;
  onSuccess?: (project: any) => void;
}

export function ProjectWizard({
  open,
  onOpenChange,
  preselectedCompanyId,
  preselectedCompanyName,
  preselectedDealType,
  onSuccess,
}: ProjectWizardProps) {
  const [wizardStep, setWizardStep] = useState(1);
  const [newName, setNewName] = useState("");
  const [newDealType, setNewDealType] = useState(preselectedDealType || "");
  const [newCompanyId, setNewCompanyId] = useState(preselectedCompanyId || "");
  const [newStartDate, setNewStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [newTargetClose, setNewTargetClose] = useState("");
  const [newDealSize, setNewDealSize] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [autoFillMilestones, setAutoFillMilestones] = useState(true);
  const [wizardAnswers, setWizardAnswers] = useState<Record<string, boolean>>({});

  const createMutation = useCreateProject();

  const { data: companies = [] } = useQuery<{ id: string; name: string; category: string }[]>({
    queryKey: ["companies"],
    queryFn: async () => {
      const res = await fetch("/api/companies", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: open && !preselectedCompanyId,
  });

  const resetWizard = () => {
    setWizardStep(1);
    setNewName("");
    setNewDealType(preselectedDealType || "");
    setNewCompanyId(preselectedCompanyId || "");
    setNewStartDate(new Date().toISOString().split("T")[0]);
    setNewTargetClose("");
    setNewDealSize("");
    setNewDescription("");
    setAutoFillMilestones(true);
    setWizardAnswers({});
  };

  const handleCreate = () => {
    if (!newName.trim() || !newDealType) return;

    createMutation.mutate(
      {
        name: newName.trim(),
        startDate: new Date(newStartDate).toISOString(),
        dealType: newDealType,
        companyId: newCompanyId || undefined,
        targetCloseDate: newTargetClose || undefined,
        dealSize: newDealSize || undefined,
        description: newDescription || undefined,
        autoFill: autoFillMilestones,
        wizardAnswers,
      } as any,
      {
        onSuccess: (data) => {
          toast.success("Project created");
          onOpenChange(false);
          resetWizard();
          onSuccess?.(data);
        },
        onError: (err: any) => toast.error(err.message),
      }
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetWizard();
    onOpenChange(nextOpen);
  };

  const relevantQuestions = WIZARD_QUESTIONS.filter((q) => q.dealTypes.includes(newDealType));
  const canAdvanceStep1 = newName.trim() && newDealType;

  const selectedCompanyName = preselectedCompanyName || companies.find((c) => c.id === newCompanyId)?.name;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            New Project
            <div className="flex gap-1 ml-auto">
              {[1, 2, 3].map((s) => (
                <div key={s} className={cn(
                  "w-8 h-1.5 rounded-full transition-colors",
                  s <= wizardStep ? "bg-primary" : "bg-muted"
                )} />
              ))}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Basics */}
        {wizardStep === 1 && (
          <div className="space-y-5 py-2">
            <p className="text-sm text-muted-foreground">
              Let's start with the basics. What deal are we working on?
            </p>

            {/* Client */}
            {preselectedCompanyId ? (
              <div className="space-y-2">
                <Label>Client</Label>
                <div className="p-2.5 rounded-md border bg-muted/30 text-sm font-medium">
                  {preselectedCompanyName || "Selected Client"}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Client / Company</Label>
                <Select value={newCompanyId} onValueChange={setNewCompanyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Link to a CRM client (optional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companies
                      .filter((c) => ["Client", "Prospects"].includes(c.category))
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Project Name */}
            <div className="space-y-2">
              <Label>Project Name <span className="text-red-500">*</span></Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Acme Corp – Sell-Side M&A"
                autoFocus
              />
            </div>

            {/* Deal Type */}
            <div className="space-y-2">
              <Label>Deal Type <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-2">
                {DEAL_TYPES.map((dt) => (
                  <button
                    key={dt.value}
                    type="button"
                    onClick={() => setNewDealType(dt.value)}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      newDealType === dt.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <p className="font-medium text-sm">{dt.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{dt.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Deal Size & Dates */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Deal Size</Label>
                <Select value={newDealSize} onValueChange={setNewDealSize}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Size..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Start Date</Label>
                <Input type="date" className="h-9 text-xs" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Target Close</Label>
                <Input type="date" className="h-9 text-xs" value={newTargetClose} onChange={(e) => setNewTargetClose(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Status Questions */}
        {wizardStep === 2 && (
          <div className="space-y-5 py-2">
            <p className="text-sm text-muted-foreground">
              Where are we in the process? This helps us set up the right milestones.
            </p>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
              <div>
                <Label className="font-medium">Auto-generate milestones</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {autoFillMilestones
                    ? newDealType === "M&A"
                      ? "Uses FTC's M&A template — adjusted based on your answers below."
                      : "AI will generate milestones tailored to this deal."
                    : "Start with a blank project — add milestones manually."}
                </p>
              </div>
              <Switch checked={autoFillMilestones} onCheckedChange={setAutoFillMilestones} />
            </div>

            {autoFillMilestones && relevantQuestions.length > 0 && (
              <div className="space-y-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Current Status
                </Label>
                {relevantQuestions.map((q) => (
                  <label
                    key={q.id}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      wizardAnswers[q.id] ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    )}
                  >
                    <Checkbox
                      checked={wizardAnswers[q.id] || false}
                      onCheckedChange={(checked) =>
                        setWizardAnswers((prev) => ({ ...prev, [q.id]: checked as boolean }))
                      }
                    />
                    <span className="text-sm">{q.question}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label>Additional Context (optional)</Label>
              <Textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Anything else relevant — industry nuances, seller timeline preferences, key parties..."
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Step 3: Review & Create */}
        {wizardStep === 3 && (
          <div className="space-y-5 py-2">
            <p className="text-sm text-muted-foreground">
              Review your project details. You can always adjust milestones after creation.
            </p>

            <Card>
              <CardContent className="p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Project</span>
                  <span className="font-medium">{newName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deal Type</span>
                  <Badge variant="secondary">{newDealType}</Badge>
                </div>
                {selectedCompanyName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Client</span>
                    <span className="font-medium">{selectedCompanyName}</span>
                  </div>
                )}
                {newDealSize && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deal Size</span>
                    <span>{newDealSize}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Date</span>
                  <span>{new Date(newStartDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                </div>
                {newTargetClose && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Target Close</span>
                    <span>{new Date(newTargetClose).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Milestones</span>
                  <span className="flex items-center gap-1">
                    {autoFillMilestones ? (
                      <>
                        <Sparkles className="w-3 h-3 text-primary-text" />
                        {newDealType === "M&A" ? "FTC template (27 milestones)" : "AI-generated"}
                      </>
                    ) : (
                      "Start blank"
                    )}
                  </span>
                </div>

                {autoFillMilestones && Object.entries(wizardAnswers).filter(([, v]) => v).length > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <span className="text-xs text-muted-foreground">Already completed:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(wizardAnswers)
                        .filter(([, v]) => v)
                        .map(([qId]) => {
                          const q = WIZARD_QUESTIONS.find((wq) => wq.id === qId);
                          return q ? (
                            <Badge key={qId} variant="outline" className="text-[10px]">
                              <CheckCircle2 className="w-2.5 h-2.5 mr-1 text-green-500" />
                              {q.question.replace("Has ", "").replace(" already been ", " ").replace("?", "")}
                            </Badge>
                          ) : null;
                        })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter className="gap-2">
          {wizardStep > 1 && (
            <Button variant="outline" onClick={() => setWizardStep((s) => s - 1)} className="gap-1 mr-auto">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          )}
          {wizardStep < 3 ? (
            <Button
              onClick={() => setWizardStep((s) => s + 1)}
              disabled={wizardStep === 1 && !canAdvanceStep1}
              className="gap-1"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={createMutation.isPending} className="gap-1">
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {autoFillMilestones && newDealType !== "M&A" ? "Generating..." : "Creating..."}
                </>
              ) : (
                "Create Project"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
